import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
  ButtonGroup,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import { Save, Cancel, CheckCircle, Error as ErrorIcon, Edit, Delete, DeleteOutline, Settings as SettingsIcon } from '@mui/icons-material';
import LoadingButton from '../components/LoadingButton';
import BulkEditModal from '../components/BulkEditModal';
import { useNotification } from '../hooks/useNotification';
import { useUserSettings } from '../hooks/useUserSettings';
import { jobsApi, categoriesApi, tagsApi, reviewApi } from '../services/api';
import TagSelector from '../components/TagSelector';
import AgentRulesModal from '../components/AgentRulesModal';
import { kindsFor, emailTypeLabel } from '../utils/emailTypes';
import { formatMoney } from '../utils/money';
import { categoryMenuItems } from '../utils/categoryMenu';

// The kind a category implies for a transaction of this type (null: keep the current one)
const kindFromCategory = (category, type) => {
  if (!category || !category.kind) return null;
  const kind = category.kind === 'spending' ? 'expense' : category.kind;
  if (type === 'credit' && kind === 'expense') return null;
  if (type === 'expense' && kind === 'income') return null;
  return kind;
};

// How often the combined review checks for transactions that arrived meanwhile
const ARRIVALS_POLL_MS = 30000;

/**
 * Review one job's transactions (/review-job/:jobId), or everything read from
 * emails together (/review, no jobId). The combined review is a snapshot:
 * saving covers only the rows loaded here; rows that arrive meanwhile are
 * counted in a banner and wait for the next review.
 */
const ReviewJob = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError, showInfo } = useNotification();
  const { canChooseModels } = useUserSettings();

  const isInbox = !jobId;
  const jobType = isInbox ? 'email' : (searchParams.get('type') || 'pdf'); // Default to PDF for backward compatibility
  // Combined review: every row id loaded (saved, skipped or removed here) and when the newest arrived
  const [snapshot, setSnapshot] = useState({ seenIds: [], asOf: null });
  const [arrived, setArrived] = useState(0);

  const [job, setJob] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [agentModal, setAgentModal] = useState({ open: false, sourceText: '' });

  useEffect(() => {
    loadJob();
    loadCategories();
    loadTags();
  }, [jobId]);

  // Combined review: count what arrives while the user is reviewing (not added to the list)
  const checkArrivals = useCallback(async () => {
    if (!isInbox || !snapshot.asOf) return;
    try {
      const response = await reviewApi.summary(snapshot.asOf);
      setArrived(response.data.arrivedSince || 0);
    } catch (error) {
      // Checked again on the next poll
    }
  }, [isInbox, snapshot]);

  useEffect(() => {
    if (!isInbox) return undefined;
    const timer = setInterval(checkArrivals, ARRIVALS_POLL_MS);
    return () => clearInterval(timer);
  }, [isInbox, checkArrivals]);

  const loadJob = async () => {
    try {
      let response;
      if (isInbox) {
        response = await reviewApi.get();
        const rows = response.data.transactions || [];
        setJob({ inbox: true, jobIds: response.data.jobIds || [] });
        setSnapshot({ seenIds: rows.map(t => t.id), asOf: response.data.asOf || null });
        setArrived(0);
        setSelectedIndices([]);
        setTransactions(rows.map(t => ({ ...t, skip: !!(t.duplicateOfTransactionId || t.duplicateReason) })));
        setLoading(false);
        return;
      }
      if (jobType === 'email') {
        response = await jobsApi.getEmailJob(jobId);
      } else {
        response = await jobsApi.getOne(jobId);
      }
      setJob(response.data.job);
      // Rows that look like saved transactions are skipped unless the user keeps them
      setTransactions((response.data.transactions || []).map(t => ({ ...t, skip: !!(t.duplicateOfTransactionId || t.duplicateReason) })));
      setLoading(false);
    } catch (error) {
      console.error('Error loading job:', error);
      if (isInbox) {
        showError('Failed to load the review');
        setLoading(false);
        return;
      }
      showError('Failed to load job details');
      navigate('/processing-jobs');
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      // Handle different response formats
      const categoriesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.categories || []);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]); // Ensure it's always an array
    }
  };

  const loadTags = async () => {
    try {
      const response = await tagsApi.getAll();
      setTags(response.data.tags || []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };

  const withCategoryKind = (transaction, categoryName) => {
    // A new category brings its kind (e.g. "Investments" -> investment) unless the kind was set by hand
    if (transaction.kindEdited) return transaction;
    const kind = kindFromCategory(categories.find(c => c.name === categoryName), transaction.type);
    return kind ? { ...transaction, kind } : transaction;
  };

  const handleTransactionChange = (index, field, value) => {
    const updatedTransactions = [...transactions];
    let updated = { ...updatedTransactions[index], [field]: value };
    if (field === 'category') updated = withCategoryKind(updated, value);
    if (field === 'kind') updated.kindEdited = true;
    updatedTransactions[index] = updated;
    setTransactions(updatedTransactions);
  };

  const handleSelectAll = () => {
    if (selectedIndices.length === transactions.length) {
      setSelectedIndices([]);
    } else {
      setSelectedIndices(transactions.map((_, index) => index));
    }
  };

  const handleSelectTransaction = (index) => {
    setSelectedIndices(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleBulkEdit = (updates) => {
    const updatedTransactions = [...transactions];
    selectedIndices.forEach(index => {
      let updated = { ...updatedTransactions[index], ...updates };
      if (updates.category) updated = withCategoryKind(updated, updates.category);
      updatedTransactions[index] = updated;
    });
    setTransactions(updatedTransactions);
    setSelectedIndices([]);
    setBulkEditDialog(false);
    showSuccess(`Updated ${selectedIndices.length} transactions`);
  };

  const handleBulkDelete = () => {
    if (!window.confirm(`Delete ${selectedIndices.length} selected transaction(s)? This action cannot be undone.`)) {
      return;
    }
    
    // Remove selected transactions
    const updatedTransactions = transactions.filter((_, index) => !selectedIndices.includes(index));
    setTransactions(updatedTransactions);
    showSuccess(`${selectedIndices.length} transaction(s) removed`);
    setSelectedIndices([]);
  };

  const handleDeleteSingle = (index) => {
    if (!window.confirm('Delete this transaction? This action cannot be undone.')) {
      return;
    }
    
    const updatedTransactions = transactions.filter((_, i) => i !== index);
    setTransactions(updatedTransactions);
    
    // Update selected indices
    setSelectedIndices(prev => 
      prev
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i)
    );
    
    showSuccess('Transaction removed');
  };

  const toSave = transactions.filter(t => !t.skip);
  const skippedCount = transactions.length - toSave.length;

  // Combined review: save these rows (or none) and discard the rest of what was loaded
  const commitInbox = async (rows) => {
    const response = await reviewApi.commit(snapshot.seenIds, rows);
    const { saved = 0, alreadyReviewed = 0, waiting = 0 } = response.data;
    const parts = [saved ? `${saved} transaction${saved === 1 ? '' : 's'} saved` : 'Review cleared'];
    if (alreadyReviewed) parts.push(`${alreadyReviewed} had already been reviewed elsewhere`);
    showSuccess(parts.join('; '));
    if (waiting > 0) {
      // What arrived during this review is shown now, as a new review
      showInfo(`${waiting} more transaction${waiting === 1 ? '' : 's'} arrived while you were reviewing`);
      setLoading(true);
      await loadJob();
    } else if (saved > 0) {
      navigate('/transactions');
    } else {
      await loadJob();
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      // kindEdited and skip are only for this screen
      const payload = toSave.map(({ skip, kindEdited, sourceSubject, sourceReceivedAt, ...t }) => t);
      if (isInbox) {
        await commitInbox(payload);
      } else if (jobType === 'email') {
        await jobsApi.approveEmailJob(jobId, { transactions: payload });
      } else {
        await jobsApi.approve(jobId, { transactions: payload });
      }
      
      if (toSave.length === 0) {
        showSuccess('Job dismissed successfully');
        navigate('/processing-jobs');
      } else {
        showSuccess(`${toSave.length} transactions approved and saved successfully!`);
        navigate('/transactions');
      }
    } catch (error) {
      console.error('Error approving job:', error);
      showError(isInbox ? 'Failed to save the review' : (transactions.length === 0 ? 'Failed to dismiss job' : 'Failed to approve transactions'));
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm(isInbox
      ? `Discard all ${transactions.length} transactions in this review? Ones that arrived meanwhile are kept.`
      : 'Are you sure you want to reject these transactions? This action cannot be undone.')) {
      return;
    }

    setRejecting(true);
    try {
      if (isInbox) {
        await commitInbox([]);
        return;
      }
      if (jobType === 'email') {
        await jobsApi.rejectEmailJob(jobId);
      } else {
        await jobsApi.reject(jobId);
      }
      showSuccess('Job rejected and deleted');
      navigate('/processing-jobs');
    } catch (error) {
      console.error('Error rejecting job:', error);
      showError('Failed to reject job');
    } finally {
      setRejecting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return formatMoney(parseFloat(amount) || 0);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading job details...</Typography>
      </Box>
    );
  }

  if (!job) {
    if (isInbox) return null;
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">Job not found</Typography>
        <Button onClick={() => navigate('/processing-jobs')} sx={{ mt: 2 }}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isInbox ? 'Review' : 'Review Job Results'}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        {isInbox
          ? 'Transactions read from your emails, waiting for you to save them. Bills, balances and payments from those emails are already up to date.'
          : 'Review and edit the extracted transactions below. You can modify descriptions and categories before approving, or reject to discard them entirely.'}
      </Alert>

      {isInbox && arrived > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {arrived} new transaction{arrived === 1 ? '' : 's'} arrived while you were reviewing. {arrived === 1 ? 'It is' : 'They are'} not
          in this list and will be shown after you save this review.
        </Alert>
      )}

      {!isInbox && (
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          title={`Job Details - ${job.file?.originalFilename || 'File'}`}
          subheader={`Processed: ${new Date(job.completedAt).toLocaleString()}`}
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {canChooseModels && `Model: ${job.model} | `}Pages Processed: {job.pagesProcessed} | Transactions Found: {job.transactionsFound}
          </Typography>
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader 
          title={`Review ${transactions.length} Transactions`}
          action={(isInbox && transactions.length === 0) ? null : (
            <ButtonGroup>
              <LoadingButton
                variant="outlined"
                color="error"
                loading={rejecting}
                onClick={handleReject}
                startIcon={<Cancel />}
                disabled={approving}
              >
                {isInbox ? 'Discard all' : 'Reject'}
              </LoadingButton>
              <LoadingButton
                variant="contained"
                loading={approving}
                onClick={handleApprove}
                startIcon={<Save />}
                disabled={rejecting}
              >
                {toSave.length === 0 ? (isInbox ? 'Clear review' : 'Dismiss Job') : `Approve & Save ${toSave.length}`}
              </LoadingButton>
            </ButtonGroup>
          )}
        />
        <CardContent>
          {skippedCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {skippedCount} transaction{skippedCount === 1 ? ' looks' : 's look'} already saved (or waiting in another review) and will be skipped.
              Click "Skipped" on a row to save it anyway.
            </Alert>
          )}

          {/* Bulk Actions */}
          {selectedIndices.length > 0 && (
            <Alert 
              severity="info" 
              sx={{ mb: 2 }}
              action={
                <Stack direction="row" spacing={1}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setBulkEditDialog(true)}
                    startIcon={<Edit />}
                  >
                    Edit Fields
                  </Button>
                  <Button
                    color="error"
                    size="small"
                    onClick={handleBulkDelete}
                    startIcon={<Delete />}
                  >
                    Delete
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setSelectedIndices([])}
                  >
                    Clear
                  </Button>
                </Stack>
              }
            >
              ⚡ {selectedIndices.length} transaction(s) selected
            </Alert>
          )}

          {transactions.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedIndices.length > 0 && selectedIndices.length < transactions.length}
                        checked={transactions.length > 0 && selectedIndices.length === transactions.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell>Account ID</TableCell>
                    <TableCell>Account Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction, index) => (
                    <TableRow key={transaction.id || `temp-${index}`} sx={transaction.skip ? { opacity: 0.6 } : undefined}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIndices.includes(index)}
                          onChange={() => handleSelectTransaction(index)}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(transaction.date)}
                        {transaction.time && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {transaction.time}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            transaction.originalDescription ? (
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                  Original Description:
                                </Typography>
                                <Typography variant="caption">
                                  {transaction.originalDescription}
                                </Typography>
                              </Box>
                            ) : ''
                          }
                          arrow
                          placement="top"
                          disableHoverListener={!transaction.originalDescription}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            value={transaction.description || ''}
                            onChange={(e) => handleTransactionChange(index, 'description', e.target.value)}
                            variant="outlined"
                            sx={transaction.originalDescription ? { 
                              '& .MuiOutlinedInput-root': { 
                                backgroundColor: 'action.hover' 
                              } 
                            } : {}}
                          />
                        </Tooltip>
                        {(transaction.emailType || transaction.duplicateReason) && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} alignItems="center">
                            {transaction.emailType && (
                              <Tooltip title={transaction.sourceSubject || ''} disableHoverListener={!transaction.sourceSubject}>
                                <Typography variant="caption" color="text.secondary">
                                  From: {emailTypeLabel(transaction.emailType)}
                                  {transaction.sourceReceivedAt && `, ${formatDate(transaction.sourceReceivedAt)}`}
                                </Typography>
                              </Tooltip>
                            )}
                            {transaction.duplicateReason && (
                              <Tooltip title={`${transaction.duplicateReason || 'Looks like a saved transaction'}. Click to ${transaction.skip ? 'save it anyway' : 'skip it'}.`}>
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant={transaction.skip ? 'filled' : 'outlined'}
                                  label={transaction.skip ? (transaction.duplicateOfTransactionId ? 'Skipped: already saved?' : 'Skipped: in another review') : 'Will be saved'}
                                  onClick={() => handleTransactionChange(index, 'skip', !transaction.skip)}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{transaction.bank || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{transaction.accountId || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.accountType === 'credit_card' ? 'Credit Card' : 'Bank Account'}
                          size="small"
                          variant="outlined"
                          color={transaction.accountType === 'credit_card' ? 'secondary' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>{formatCurrency(transaction.amount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.type}
                          color={transaction.type === 'credit' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        <Select
                          fullWidth
                          size="small"
                          value={transaction.kind || ''}
                          onChange={(e) => handleTransactionChange(index, 'kind', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>From category</em>
                          </MenuItem>
                          {kindsFor(transaction.type).map(k => (
                            <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Select
                          fullWidth
                          size="small"
                          value={transaction.category || ''}
                          onChange={(e) => handleTransactionChange(index, 'category', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select Category</em>
                          </MenuItem>
                          {categoryMenuItems(categories)}
                          {/* A category that is hidden or was removed still shows */}
                          {transaction.category && !categories.some(c => c.name === transaction.category) && (
                            <MenuItem value={transaction.category}>{transaction.category}</MenuItem>
                          )}
                        </Select>
                      </TableCell>
                      <TableCell sx={{ minWidth: 250 }}>
                        <TagSelector
                          value={transaction.tags || []}
                          onChange={(newTags) => handleTransactionChange(index, 'tags', newTags)}
                          allTags={tags}
                          label=""
                          placeholder="Add tags..."
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Customize agent rules">
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => {
                              setAgentModal({ open: true, sourceText: transaction.original_description || transaction.description || '' });
                            }}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete this transaction">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSingle(index)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            isInbox ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Nothing to review. Transactions read from your forwarded emails show up here.
              </Alert>
            ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                No transactions found in this job
              </Typography>
              <Typography variant="body2">
                The processing completed successfully, but no financial transactions were detected. 
                Click "Approve & Save" below to dismiss this job and remove it from the queue.
              </Typography>
            </Alert>
            )
          )}
        </CardContent>
      </Card>

      {/* Bulk Edit Modal */}
      <BulkEditModal
        open={bulkEditDialog}
        onClose={() => setBulkEditDialog(false)}
        onSave={handleBulkEdit}
        selectedCount={selectedIndices.length}
        categories={categories.map(cat => cat.name)}
      />

      {/* Agent Rules Modal */}
      <AgentRulesModal
        open={agentModal.open}
        onClose={() => setAgentModal({ open: false, sourceText: '' })}
        sourceText={agentModal.sourceText}
        agentType={jobType === 'pdf' ? 'pdf_categorization' : 'categorization'}
      />
    </Box>
  );
};

export default ReviewJob;

